import { createSignal, For, Show } from "solid-js";
import { FieldErrorCard, GenericErrorCard } from "../../../components/errorCard/ErrorCard";
import Popover from "../../../components/popover/Popover";
import NavigationService from "../../../services/NavigationService";
import { toValidationErrors } from "../../../utils/validationParserUtil";
import type { FormEditorRoute } from "../FormEditorContent";
import { useFormEditorSidePanel } from "../FormEditorContent";
import { IconButton } from "../../../components/button/Button";
import { useFormServices } from "../../../services/formServices";
import { useTranslation } from "../../../state/i18n";
import { fields } from "../../../fields/fields";
import { isEmpty, isNil } from "lodash-es";
import SubformService from "../../../services/SubformService";
import { integrationsByType } from "../../../integrations/integrations";
import toVariableDefinition from "../../../variables/toVariableDefinition";
import type { FieldLabelTextProperties } from "../../../fields/types";
import type { SubformEditorTab } from "../subforms/types";

type SectionTitleProps = { title: string };
const SectionTitle = (props: SectionTitleProps) => (
  <h3 class="px-3 pt-2 pb-0.5 text-header-md text-secondary">{props.title}</h3>
);

type HeaderValidationButtonProps = {
  activeItem: FormEditorRoute | SubformEditorTab;
};
const HeaderValidationButton = (props: HeaderValidationButtonProps) => {
  const { t } = useTranslation();
  const [openValidationPopover, setOpenValidationPopover] = createSignal(false);

  const handleClose = () => setOpenValidationPopover(false);

  return (
    <Popover
      Title={() => <h2 class="text-header-md text-primary">{t(($) => $.VALIDATION_MENU_TITLE)}</h2>}
      Trigger={(arkProps) => (
        <IconButton
          aria-label={t(($) => $.VALIDATION_MENU_TRIGGER_ARIA)}
          icon="exclamation"
          iconType="solid"
          iconClass="icon-button-icon-tertiary-destructive"
          size="large"
          variant="tertiary"
          {...arkProps}
        />
      )}
      open={openValidationPopover()}
      onOpenChange={setOpenValidationPopover}
      aria-label={t(($) => $.VALIDATION_MENU_TITLE)}
      variant="md"
      modal
      positioning={{ placement: "bottom-end" }}
    >
      <div
        tabIndex={-1}
        class="-mx-4 mt-1 -mb-4 flex flex-col gap-3 overflow-y-auto px-1 pb-4 focus-visible:outline-default"
      >
        <FieldErrorsSection onClose={handleClose} activeItem={props.activeItem} />
        <SubformErrorsSection onClose={handleClose} />
        <RuleErrorsSection onClose={handleClose} />
        <IntegrationErrorsSection onClose={handleClose} />
        <VariableErrorsSection onClose={handleClose} />
      </div>
    </Popover>
  );
};

type FieldErrorsSectionProps = {
  onClose: () => void;
  activeItem: FormEditorRoute | SubformEditorTab;
};
const FieldErrorsSection = (props: FieldErrorsSectionProps) => {
  const { t } = useTranslation();
  const { validationService, fieldService } = useFormServices();
  const { setSelectedPanel } = useFormEditorSidePanel();

  return (
    <Show when={validationService.hasFieldErrors()}>
      <section>
        <SectionTitle title={t(($) => $.VALIDATION_FIELDS_TITLE)} />
        <For each={validationService.getFieldErrors()}>
          {(error) => {
            const field = fields[error.fieldType];
            const fieldConfig = fieldService.getField<FieldLabelTextProperties>(error.fieldId);

            if (isNil(fieldConfig)) {
              return;
            }

            return (
              <FieldErrorCard
                icon={field.icon}
                category={field.category}
                label={field.displayLabel(fieldConfig.properties)}
                errors={toValidationErrors(error.issues, field.propertyKeys, t)}
                onClick={() => {
                  props.onClose();
                  NavigationService.navigateToField(
                    fieldConfig.uid,
                    props.activeItem === "fields" ? "smooth" : "instant", // Only smooth scroll when on the same page
                  );
                  setSelectedPanel({ type: "field", field, fieldConfig });
                  const panelCloseButton = document.querySelector(
                    '[data-id="panel-close-button"]',
                  ) as HTMLButtonElement | null;
                  if (!isNil(panelCloseButton)) {
                    panelCloseButton.focus();
                  }
                }}
              />
            );
          }}
        </For>
      </section>
    </Show>
  );
};

type SubformErrorsSectionProps = { onClose: () => void };
const SubformErrorsSection = (props: SubformErrorsSectionProps) => {
  const { t } = useTranslation();
  const { validationService } = useFormServices();

  return (
    <Show when={validationService.hasSubformErrors()}>
      <section>
        <SectionTitle title={t(($) => $.VALIDATION_SUBFORMS_TITLE)} />
        <For each={validationService.getSubformErrors()}>
          {(error) => {
            const subform = SubformService.getSubform(error.subformId);
            if (isNil(subform)) {
              return;
            }

            return (
              <GenericErrorCard
                icon="documentDuplicate"
                label={subform.meta.name}
                errors={[
                  {
                    message: t(($) => $.VALIDATION_SUBFORMS_ERRORS, {
                      count: error.fieldIds.length + error.ruleIds.length,
                    }),
                  },
                ]}
                onClick={() => {
                  props.onClose();
                  if (!isEmpty(error.fieldIds)) {
                    NavigationService.navigateToField(error.fieldIds[0]);
                  } else if (!isEmpty(error.ruleIds)) {
                    NavigationService.navigateToRule(error.ruleIds[0]);
                  }
                }}
              />
            );
          }}
        </For>
      </section>
    </Show>
  );
};

type RuleErrorsSectionProps = { onClose: () => void };
const RuleErrorsSection = (props: RuleErrorsSectionProps) => {
  const { t } = useTranslation();
  const { validationService } = useFormServices();

  return (
    <Show when={validationService.hasRuleErrors()}>
      <section>
        <SectionTitle title={t(($) => $.VALIDATION_RULES_TITLE)} />
        <For each={validationService.getRuleErrors()}>
          {(error) => (
            <GenericErrorCard
              icon="rulesIcon"
              label={error.label}
              errors={error.issues}
              onClick={() => {
                props.onClose();
                NavigationService.navigateToRule(error.ruleId);
              }}
            />
          )}
        </For>
      </section>
    </Show>
  );
};

type IntegrationErrorsSectionProps = { onClose: () => void };

const IntegrationErrorsSection = (props: IntegrationErrorsSectionProps) => {
  const { t } = useTranslation();
  const { validationService, integrationService } = useFormServices();

  return (
    <Show when={validationService.hasIntegrationErrors()}>
      <section>
        <SectionTitle title={t(($) => $.VALIDATION_INTEGRATIONS_TITLE)} />
        <For each={validationService.getIntegrationErrors()}>
          {(error) => {
            const integration = integrationService.getIntegration(error.integrationId);
            if (isNil(integration)) {
              return;
            }

            const integrationDefinition = integrationsByType[integration.type];

            return (
              <GenericErrorCard
                icon={integrationDefinition.icon}
                label={integration.name}
                errors={toValidationErrors(error.issues, integrationDefinition.propertyKeys, t)}
                onClick={() => {
                  props.onClose();
                  NavigationService.navigateToIntegration(integration.id);
                }}
              />
            );
          }}
        </For>
      </section>
    </Show>
  );
};

type VariableErrorsSectionProps = { onClose: () => void };
const VariableErrorsSection = (props: VariableErrorsSectionProps) => {
  const { t } = useTranslation();
  const { validationService, variableService } = useFormServices();

  return (
    <Show when={validationService.hasVariableErrors()}>
      <section>
        <SectionTitle title={t(($) => $.VALIDATION_VARIABLES_TITLE)} />
        <For each={validationService.getVariableErrors()}>
          {(error) => {
            const variableDefinition = toVariableDefinition(error.variableType);
            const variable = variableService.getVariable(error.variableId);
            if (isNil(variable)) {
              return;
            }

            return (
              <GenericErrorCard
                icon={variableDefinition.icon}
                iconColor="bg-surface-subtle icon-primary"
                label={variable.name}
                errors={toValidationErrors(error.issues, variableDefinition.propertyKeys, t)}
                onClick={() => {
                  props.onClose();
                  NavigationService.navigateToVariable(error.variableId);
                }}
              />
            );
          }}
        </For>
      </section>
    </Show>
  );
};

export default HeaderValidationButton;
